import type { ReactNode } from 'react';
import { CircleCheck, LockKeyhole, TriangleAlert } from 'lucide-react';
import { Banner } from '@/components/Banner';
import { Card, Dato } from '@/components/Card';
import { nombrePerfil, type ResumenRespaldo } from '@/db/backup-format';
import { describirUltimoRespaldo } from '@/domain/backup-reminder';
import { anio, formatearCorto } from '@/domain/dates';

interface Props {
  resumen: ResumenRespaldo;
  nombreArchivo: string;
  cifrado: boolean;
}

const ORIGEN: Record<string, string> = {
  standalone: 'App instalada',
  navegador: 'Navegador',
  'copia-interna': 'Copia interna',
};

/** Resultado de la suma con ícono y texto, no solo color (Req. 8.5). */
function verificacion(resumen: ResumenRespaldo): ReactNode {
  const correcta = resumen.traeChecksum && resumen.checksumValido;
  const texto = !resumen.traeChecksum
    ? 'Sin suma de verificación'
    : correcta
      ? 'Correcta'
      : 'No coincide';
  return (
    <span className={`inline-flex items-center gap-1.5 ${correcta ? 'text-exito' : 'text-alerta'}`}>
      {correcta ? <CircleCheck className="size-4" /> : <TriangleAlert className="size-4" />}
      {texto}
    </span>
  );
}

/**
 * Vista previa de un respaldo (Req. 6.5): perfil, fecha, registros por tipo,
 * periodo, verificación y advertencias. Analizar no toca ningún dato, así que
 * sirve también para solo revisar un archivo.
 */
export function VistaPrevia({ resumen, nombreArchivo, cifrado }: Props) {
  const conDatos = resumen.conteos.filter((c) => c.registros > 0 && c.tabla !== 'settings');
  const rango = resumen.rangoFechas;

  return (
    <div className="space-y-3">
      <Card>
        <h3 className="text-[17px] tracking-[-0.25px] text-texto">Contenido del archivo</h3>
        <p className="mb-2 mt-0.5 break-all text-[12px] text-texto-suave">{nombreArchivo}</p>
        <dl className="divide-y divide-borde">
          <Dato etiqueta="Perfil" valor={nombrePerfil(resumen.perfil)} />
          <Dato
            etiqueta="Fecha del respaldo"
            valor={resumen.exportedAt === '' ? 'Desconocida' : describirUltimoRespaldo(resumen.exportedAt)}
          />
          <Dato etiqueta="Registros" valor={String(resumen.totalRegistros)} />
          {rango !== null && (
            <Dato
              etiqueta="Periodo"
              valor={`${formatearCorto(rango.desde)} ${anio(rango.desde)} – ${formatearCorto(rango.hasta)} ${anio(rango.hasta)}`}
            />
          )}
          <Dato etiqueta="Verificación" valor={verificacion(resumen)} />
          <Dato
            etiqueta="Cifrado"
            valor={
              cifrado ? (
                <span className="inline-flex items-center gap-1.5">
                  <LockKeyhole className="size-4 text-marca" />
                  Sí
                </span>
              ) : (
                'No'
              )
            }
          />
          <Dato etiqueta="Origen" valor={ORIGEN[resumen.device] ?? 'Desconocido'} />
          {resumen.requiereMigracion && (
            <Dato
              etiqueta="Versión de los datos"
              valor={`${resumen.schemaVersion}, se actualiza al restaurar`}
            />
          )}
        </dl>

        {conDatos.length > 0 ? (
          <ul className="mt-3 space-y-1.5 border-t border-borde pt-3 text-[13px]">
            {conDatos.map((c) => (
              <li key={c.tabla} className="flex justify-between gap-4">
                <span className="text-texto-suave">{c.etiqueta}</span>
                <span className="font-medium tabular-nums text-texto">{c.registros}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 border-t border-borde pt-3 text-[13px] text-texto-suave">
            El archivo no trae registros, solo los ajustes.
          </p>
        )}
      </Card>

      {resumen.errores.length > 0 && (
        <Banner tono="alerta" titulo="Este archivo no se puede restaurar">
          <p>Tiene registros dañados que la app no puede guardar:</p>
          <ul className="mt-1 list-disc space-y-1 pl-4">
            {resumen.errores.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </Banner>
      )}

      {resumen.advertencias.length > 0 && (
        <Banner
          tono={resumen.exigeConfirmacionReforzada ? 'alerta' : 'aviso'}
          titulo={
            resumen.exigeConfirmacionReforzada
              ? 'El archivo no pasó la verificación'
              : 'Revisa estas advertencias'
          }
        >
          <ul className="list-disc space-y-1 pl-4">
            {resumen.advertencias.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </Banner>
      )}
    </div>
  );
}
